import { collection, doc, runTransaction, serverTimestamp, getDoc } from "firebase/firestore"
import { db } from "./firebase"
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service"
import XpService, { XP_ACTION_TYPES } from "./xp_service"
import { saleToFirestore, saleFromFirestore } from "../models/sale"
import { toISODate } from "../utils/date"
import { COLLECTIONS } from "../services/collections";

/**
 * SalesService
 * Gerencia o processamento de vendas, criação de memberships e lançamentos financeiros.
 */
const SalesService = {
    /**
     * Busca uma venda por ID.
     */
    getById: async (idTenant, idSale) => {
        if (!idTenant || !idSale) return null;
        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.SALES, idSale);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return saleFromFirestore(docSnap);
            }
            return null;
        } catch (error) {
            console.error("Erro ao buscar venda:", error);
            throw error;
        }
    },

    /**
     * Processa uma venda completa de forma atômica usando transações.
     */
    processSale: async (idTenant, idUser, payload) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.")

        try {
            // Converte a Payload da requisição (Que deve vir semelhante a saleDefaults) pro Formato Firestore
            const tenantInfo = { id: idTenant };
            const authUser = { uid: idUser, name: payload.employeeName };
            const saleData = saleToFirestore(payload, tenantInfo, authUser);

            // Usamos uma transação para garantir que se um passo falhar, nada seja salvo.
            // REGRA Firestore: todas as leituras (get) ANTES de todas as escritas (set/update).
            const result = await runTransaction(db, async (transaction) => {

                // ═══════════════════════════════════════
                // FASE 1: LEITURAS (obrigatoriamente primeiro)
                // ═══════════════════════════════════════
                const hasMembershipItem = payload.items.some(item => item.type === 'membership');
                let memberSnap = null;
                let memberRef = null;

                if (hasMembershipItem && payload.idMember) {
                    memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, payload.idMember);
                    memberSnap = await transaction.get(memberRef);
                }

                // 2. Leitura do Contador Sequencial de Vendas
                const counterRef = doc(db, "tenants", idTenant, COLLECTIONS.COUNTERS, "sales");
                const counterSnap = await transaction.get(counterRef);

                // ═══════════════════════════════════════
                // FASE 2: ESCRITAS (após todas as leituras)
                // ═══════════════════════════════════════

                // Incrementar contador de vendas
                let saleSequence = 1000;
                if (counterSnap.exists() && typeof counterSnap.data().lastNumber === 'number') {
                    saleSequence = counterSnap.data().lastNumber + 1;
                }
                const formattedSaleNumber = String(saleSequence).padStart(5, '0');
                transaction.set(counterRef, { lastNumber: saleSequence }, { merge: true });

                // A. Salvar a Venda
                const saleRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.SALES));
                
                // Grava a venda com o número gerado atrelado no BD
                saleData.saleNumber = formattedSaleNumber;
                transaction.set(saleRef, saleData);

                // Variável para armazenar os IDs criados para poder vincular as matrículas posteriormente
                const createdMemberships = {};

                // B. Criar registros de memberMembership para cada plano vendido
                for (const item of payload.items) {
                    if (item.type === 'membership') {
                        const memberMembershipRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS));

                        // Se a venda tiver cartId, guardamos o reference ID criado
                        if (item.cartId) {
                            createdMemberships[item.cartId] = memberMembershipRef.id;
                        }

                        // Determinar a Duração e data final
                        const duration = parseInt(item.duration) || 1;
                        const durationType = item.durationType || "monthly";
                        const start = new Date(item.startDate);
                        const end = new Date(start);

                        // Cálculo de endDate
                        if (durationType === "monthly") {
                            end.setMonth(end.getMonth() + duration);
                        } else if (durationType === "yearly") {
                            end.setFullYear(end.getFullYear() + duration);
                        } else if (durationType === "days") {
                            end.setDate(end.getDate() + duration);
                        } else {
                            // fallback para "classes" e outros
                            end.setMonth(end.getMonth() + 1);
                        }

                        const actualMemberData = (memberSnap && memberSnap.exists()) ? memberSnap.data() : {};

                        const membershipPayload = {
                            idTenant,
                            idSale: saleRef.id,
                            idMember: payload.idMember, // Firestore document ID do membro
                            memberNumber: payload.memberNumber || "",       // ID sequencial do membro (ex: "0001")
                            memberName: payload.memberName,
                            memberEmail: actualMemberData.email || payload.memberEmail || "",
                            memberPhone: actualMemberData.phone || payload.memberPhone || "",
                            memberPhotoUrl: actualMemberData.photoUrl || "",

                            idMembership: item.idItem,
                            membershipName: item.name,
                            type: item.membershipType || "recurrent",
                            durationType,
                            duration,
                            enrollmentRequired: !!item.enrollmentRequired,
                            memberXp: parseInt(item.memberXp) || 0,
                            employeeXp: parseInt(item.employeeXp) || 0,

                            startDate: toISODate(start),
                            endDate: toISODate(end),

                            originalValue: item.unitPrice || 0,
                            discount: 0,
                            price: item.totalPrice || item.unitPrice || 0,
                            isScholarship: !!item.isScholarship,

                            status: "active",

                            allowsSuspension: !!item.allowsSuspension,
                            maxSuspensionDays: parseInt(item.maxSuspensionDays) || 0,
                            disponibleSuspensionDays: parseInt(item.maxSuspensionDays) || 0,
                            numberSuspensionTimes: 0,
                            suspensionFee: parseFloat(item.suspensionFee) || 0,
                            freezes: [],

                            cancellationFee: parseFloat(item.cancellationFee) || 0,
                            cancellationNoticeDays: parseInt(item.cancellationNoticeDays) || 0,
                            allowsCancellationByApp: !!item.allowsCancellationByApp,
                            cancelDate: null,
                            cancelReason: "",

                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                            createdBy: { uid: idUser, name: payload.employeeName },
                            updatedBy: { uid: idUser, name: payload.employeeName }
                        };

                        transaction.set(memberMembershipRef, membershipPayload);

                        // ── XP do Membro: creditar automaticamente ──
                        const memberXpAmount = parseInt(item.memberXp) || 0;
                        if (memberXpAmount > 0 && payload.idMember) {
                            XpService.addXpInTransaction(transaction, idTenant, {
                                idMember: payload.idMember,
                                xpAmount: memberXpAmount,
                                actionType: XP_ACTION_TYPES.MEMBERSHIP_PURCHASE,
                                sourceId: memberMembershipRef.id,
                                sourceName: item.name,
                                user: authUser
                            });
                        }
                    }
                }

                // C. Se vendeu um plano, ativar o membro (status → "active")
                if (memberSnap && memberSnap.exists()) {
                    const currentStatus = memberSnap.data().status;
                    if (currentStatus !== "active") {
                        transaction.update(memberRef, {
                            status: "active",
                            updatedAt: serverTimestamp(),
                            updatedBy: { uid: idUser, name: payload.employeeName }
                        });
                    }
                }

                return { saleId: saleRef.id, saleNumber: formattedSaleNumber, createdMemberships }
            })

            // 2. Auditoria (fora da transação para não bloquear)
            AuditService.log({
                idTenant,
                user: { uid: idUser, displayName: payload.employeeName },
                module: AUDIT_MODULES.SALES,
                action: AUDIT_ACTIONS.CREATE,
                entityId: result.saleId,
                entityName: `Venda #${result.saleNumber}`,
                description: `Realizou uma venda para o membro ${payload.memberName}`,
                details: { total: payload.totalAmount, itemsCount: payload.items.length }
            })

            return result;

        } catch (error) {
            console.error("Erro no SalesService.processSale:", error)
            throw error
        }
    }
}

export default SalesService
